import React from 'react';

import { connect } from 'react-redux';

import { get } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';
import AutoComplete from '../../components/FormFields/Autocomplete';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as DeviceActions from '../../actions/deviceActions';
import * as CoreActions from '../../actions/coreActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    name: Yup.string()
        .max(45)
        .required(),
    profile: Yup.string()
        .required(),
    model:    Yup.string(),
    deviceId: Yup.number()
        .required(),
    macAddress: Yup.string()
});

const CreateBacnetDeviceModal = ({
    close,
    clearErrors,
    profiles,
    open,
    isLoading,
    toggleLoading,
    errors,
    ...props
}) => {
    const formik = useFormik({
        initialValues: {
            name:       '',
            profile:    '',
            model:      '',
            deviceId:   '',
            macAddress: ''
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                agent,
                createDevice,
                fetchDevices,
                companyInfo,
                enqueueSnackbar
            } = props;

            const agentId = get(agent, 'id', '');
            const basType = get(agent, 'driverConfiguration[0].basType', '');
            const configId = get(agent, 'driverConfiguration[0].id', '');

            const body = {
                name:            formik.values.name,
                basProfileId:    formik.values.profile.id,
                configurationId: configId,
                model:           formik.values.model,
                deviceId:        formik.values.deviceId,
                macAddress:      formik.values.macAddress
            };

            let isCreated;

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createDevice(agentId, body);
                }

                if (isCreated) {
                    handleClose();
                    enqueueSnackbar('Device created successfully', 'success');

                    await fetchDevices(companyInfo.companyId, agentId, configId, basType);
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }

            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        close();
    };

    return (
        <EsmsDialog
            onClose={close}
            disabled={formik.isSubmitting}
            onConfirm={formik.handleSubmit}
            confirmText="Create"
            open={open}
            isLoading={isLoading}
            title="Create Bacnet Device"
            cancelable={true}
            errors={errors}
            onRendered={formik.handleReset}
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/nodesIconColor.svg" alt="Devices Icon" />
                        <span>Create Bacnet Device</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Provide a Device name.
                        </p>
                        <p>
                            Select Profile. The Profile will be used to create Device Objects for this Device.
                        </p>
                        <p>
                            Note: A Device must have a Profile, if you have not created any Profiles, create a Profile first.
                        </p>
                        <p>
                            Provide a Model for this Device.
                        </p>
                        <p>
                            Provide a Device ID and a MAC Address for this Device.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.fullWidth}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <AutoComplete
                            grouped
                            label="Profile"
                            name="profile"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            options={profiles}
                            placeholder="Profile"
                            value={formik.values.profile}
                            error={Boolean(formik.errors.profile) && Boolean(formik.touched.profile)}
                            helperText={Boolean(formik.touched.profile) && formik.errors.profile}
                        />
                        <FormControl className={classes.fullWidth}>
                            <TextField
                                id="model"
                                name="model"
                                type="text"
                                value={formik.values.model}
                                label="Model"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.model) && Boolean(formik.touched.model)}
                                helperText={Boolean(formik.touched.model) && formik.errors.model}
                            />
                        </FormControl>
                        <FormControl className={classes.fullWidth}>
                            <TextField
                                id="deviceId"
                                name="deviceId"
                                type="text"
                                value={formik.values.deviceId}
                                label="Device Id"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.deviceId) && Boolean(formik.touched.deviceId)}
                                helperText={Boolean(formik.touched.deviceId) && formik.errors.deviceId}
                            />
                        </FormControl>
                        <FormControl className={classes.fullWidth}>
                            <TextField
                                id="macAddress"
                                name="macAddress"
                                type="text"
                                value={formik.values.macAddress}
                                label="MAC"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.macAddress) && Boolean(formik.touched.macAddress)}
                                helperText={Boolean(formik.touched.macAddress) && formik.errors.macAddress}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

const mapStateToProps = state => {
    return {
        companyInfo: state.companyInfo,
        errors:      state.messages.errors,
        isLoading:   state.ui.isLoading
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...DeviceActions,
    ...CoreActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateBacnetDeviceModal);
